// utils/processors/carouselMediaProcessor.js

export class CarouselMediaProcessor {
  static processCarouselMedia(tempDiv) {
    // Regex patterns for different media types
    const imagePattern = /https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg|bmp|ico)(?:\?[^\s]*)?/gi;
    const videoPattern = /https?:\/\/[^\s]+\.(?:mp4|webm|ogg|mov|avi|wmv|flv|mkv)(?:\?[^\s]*)?/gi;
    const youtubePattern = /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/gi;

    // Process all lists to group consecutive media list items
    const lists = tempDiv.querySelectorAll('ul, ol');
    lists.forEach(list => {
      const listItems = Array.from(list.querySelectorAll('li'));
      let mediaGroups = [];
      let currentGroup = [];

      listItems.forEach((li, index) => {
        // Get only the direct text content of this specific list item
        let itemText = '';
        for (let child of li.childNodes) {
          if (child.nodeType === Node.TEXT_NODE) {
            itemText += child.textContent;
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            if (!['UL', 'OL', 'LI'].includes(child.tagName)) {
              itemText += child.textContent;
            }
          }
        }

        // Check for media URLs in this specific list item's text only
        const imageMatches = itemText.match(imagePattern) || [];
        const videoMatches = itemText.match(videoPattern) || [];
        const youtubeMatches = itemText.match(youtubePattern) || [];

        const mediaUrls = [...imageMatches, ...videoMatches, ...youtubeMatches];
        const hasMedia = mediaUrls.length > 0;

        if (hasMedia) {
          // Extract alignment classes from the list item
          const alignmentClasses = [];
          if (li.classList.contains('ql-align-center')) alignmentClasses.push('ql-align-center');
          if (li.classList.contains('ql-align-right')) alignmentClasses.push('ql-align-right');
          if (li.classList.contains('ql-align-justify')) alignmentClasses.push('ql-align-justify');

          // Add to current group
          currentGroup.push({
            element: li,
            text: itemText,
            mediaUrls: mediaUrls,
            imageMatches: imageMatches,
            videoMatches: videoMatches,
            youtubeMatches: youtubeMatches,
            alignmentClasses: alignmentClasses
          });
        } else {
          // If we have a group, save it and start a new one
          if (currentGroup.length > 0) {
            mediaGroups.push(currentGroup);
            currentGroup = [];
          }
        }

        // If this is the last item and we have a group, save it
        if (index === listItems.length - 1 && currentGroup.length > 0) {
          mediaGroups.push(currentGroup);
        }
      });

      // Process each media group
      mediaGroups.forEach(group => {
        if (group.length === 0) return;

        // Create horizontal media layout for the group
        let mediaHTML = '';
        let groupText = '';

        group.forEach(item => {
          // Extract text without URLs
          let textWithoutUrls = item.text;
          item.mediaUrls.forEach(url => {
            textWithoutUrls = textWithoutUrls.replace(url, '').trim();
          });
          if (textWithoutUrls) {
            groupText += (groupText ? ' • ' : '') + textWithoutUrls;
          }

          // Add images to horizontal layout
          item.imageMatches.forEach(url => {
            mediaHTML += CarouselMediaProcessor.generateCarouselImageHTML(url);
          });

          // Add videos to horizontal layout
          item.videoMatches.forEach(url => {
            mediaHTML += CarouselMediaProcessor.generateCarouselVideoHTML(url);
          });

          // Add YouTube videos to horizontal layout
          item.youtubeMatches.forEach(match => {
            const videoId = match.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1];
            if (videoId) {
              mediaHTML += CarouselMediaProcessor.generateCarouselYouTubeHTML(videoId);
            }
          });
        });

        // Replace the first item in the group with the horizontal layout
        const firstItem = group[0].element;
        firstItem.classList.add('has-carousel-media');

        // Get alignment classes from the group
        const groupAlignment = group[0].alignmentClasses || [];
        const alignmentClassString = groupAlignment.join(' ');

        // Check if we need carousel (more than 3 items)
        const totalMediaCount = group.reduce((count, item) => 
          count + item.imageMatches.length + item.videoMatches.length + item.youtubeMatches.length, 0
        );

        const needsCarousel = totalMediaCount > 3;
        const carouselId = `carousel-${Math.random().toString(36).substr(2, 9)}`;

        let finalHTML = '';
        if (groupText) {
          finalHTML += `<div class="carousel-media-text ${alignmentClassString}">${groupText}</div>`;
        }

        if (needsCarousel) {
          finalHTML += CarouselMediaProcessor.generateCarouselHTML(carouselId, mediaHTML, alignmentClassString);
        } else {
          finalHTML += `<div class="carousel-media-horizontal ${alignmentClassString}">${mediaHTML}</div>`;
        }

        firstItem.innerHTML = finalHTML;

        // Remove the other items in the group
        for (let i = 1; i < group.length; i++) {
          group[i].element.remove();
        }
      });
    });

    return tempDiv;
  }

  static generateCarouselImageHTML(url) {
    return `
      <div class="carousel-media-item">
        <img src="${url}" alt="Image" 
             onclick="window.open('${url}', '_blank')" 
             class="carousel-media-image" />
      </div>
    `;
  }

  static generateCarouselVideoHTML(url) {
    return `
      <div class="carousel-media-item">
        <video controls class="carousel-media-video">
          <source src="${url}" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      </div>
    `;
  }

  static generateCarouselYouTubeHTML(videoId) {
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    return `
      <div class="carousel-media-item">
        <div class="carousel-media-youtube-wrapper">
          <iframe src="${embedUrl}" 
                  class="carousel-media-youtube-iframe" 
                  frameborder="0" 
                  allowfullscreen>
          </iframe>
        </div>
      </div>
    `;
  }

  static generateCarouselHTML(carouselId, mediaHTML, alignmentClassString) {
    return `
      <div class="carousel-media-container ${alignmentClassString}">
        <div class="carousel-media-nav carousel-prev" onclick="scrollCarousel('${carouselId}', -1)">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
        </div>
        <div id="${carouselId}" class="carousel-media-horizontal">${mediaHTML}</div>
        <div class="carousel-media-nav carousel-next" onclick="scrollCarousel('${carouselId}', 1)">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </div>
      </div>
    `;
  }

  static getStyles() {
    return `
      /* Carousel Media Styles */
      .rich-text-content .carousel-media-text {
        margin-bottom: 0.5em !important;
      }

      .rich-text-content .carousel-media-container {
        position: relative !important;
        margin: 1rem 0 !important;
        padding: 0 2.5rem !important;
      }

      .rich-text-content .carousel-media-horizontal {
        display: flex !important;
        gap: 1rem !important;
        overflow-x: auto !important;
        scroll-behavior: smooth !important;
        padding: 0.5rem !important;
        -webkit-overflow-scrolling: touch !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }

      .rich-text-content .carousel-media-horizontal::-webkit-scrollbar {
        display: none !important;
      }

      .rich-text-content .carousel-media-item {
        flex: 0 0 280px !important;
        height: 200px !important;
        border-radius: 8px !important;
        overflow: hidden !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .rich-text-content .carousel-media-image {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        cursor: pointer !important;
        margin: 0 !important;
        padding: 0 !important;
        display: block !important;
      }

      .rich-text-content .carousel-media-video {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        margin: 0 !important;
        padding: 0 !important;
        display: block !important;
      }

      .rich-text-content .carousel-media-youtube-wrapper {
        position: relative !important;
        width: 100% !important;
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .rich-text-content .carousel-media-youtube-iframe {
        width: 100% !important;
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
      }

      .rich-text-content .carousel-media-nav {
        position: absolute !important;
        top: 50% !important;
        transform: translateY(-50%) !important;
        background: rgba(0, 0, 0, 0.7) !important;
        color: white !important;
        border: none !important;
        border-radius: 50% !important;
        width: 40px !important;
        height: 40px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        cursor: pointer !important;
        transition: background-color 0.2s ease !important;
        z-index: 10 !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      .rich-text-content .carousel-media-nav:hover {
        background: rgba(0, 0, 0, 0.9) !important;
      }

      .rich-text-content .carousel-prev {
        left: 0 !important;
      }

      .rich-text-content .carousel-next {
        right: 0 !important;
      }

      .rich-text-content .carousel-media-nav svg {
        width: 20px !important;
        height: 20px !important;
      }

      /* Mobile Responsiveness for Carousel Media */
      @media (max-width: 768px) {
        .rich-text-content .carousel-media-container {
          padding: 0 1.5rem !important;
        }

        .rich-text-content .carousel-media-item {
          flex: 0 0 240px !important;
          height: 160px !important;
        }

        .rich-text-content .carousel-media-nav {
          width: 36px !important;
          height: 36px !important;
        }

        .rich-text-content .carousel-media-nav svg {
          width: 18px !important;
          height: 18px !important;
        }
      }

      @media (max-width: 480px) {
        .rich-text-content .carousel-media-container {
          padding: 0 1rem !important;
        }

        .rich-text-content .carousel-media-item {
          flex: 0 0 200px !important;
          height: 140px !important;
        }

        .rich-text-content .carousel-media-nav {
          width: 32px !important;
          height: 32px !important;
        }

        .rich-text-content .carousel-media-nav svg {
          width: 16px !important;
          height: 16px !important;
        }
      }
    `;
  }

  // Add scroll carousel function to window
  static initializeCarouselFunctions() {
    if (typeof window !== 'undefined') {
      window.scrollCarousel = function(carouselId, direction) {
        const carousel = document.getElementById(carouselId);
        if (carousel) {
          const scrollAmount = 300;
          carousel.scrollBy({
            left: direction * scrollAmount,
            behavior: 'smooth'
          });
        }
      };
    }
  }
}